import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, Save, Trash2, ChevronUp, ChevronDown, Loader2, Coins, Euro, Power, X, ShieldAlert,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import {
  listCreatorPriceOptions,
  createCreatorPriceOption,
  updateCreatorPriceOption,
  deleteCreatorPriceOption,
  reorderCreatorPriceOptions,
  getO2OPriceLimit,
  updateO2OPriceLimit,
} from '../../api/creatorPrices';

/**
 * Admin — Creator Price Options (scale separate per ambito).
 *
 * Ambiti supportati:
 *  - o2o (One-to-One, con limite massimo admin-editable, default €1,50/min)
 *  - live (Live programmate)
 *  - post_image / post_video / post_audio (contenuti feed per tipo media)
 *  - post (legacy: vale per tutti i tipi di post)
 *  - all (vale ovunque)
 *
 * Business rules:
 * - Disattivare un prezzo NON modifica i contenuti già pubblicati.
 * - Il limite O2O blocca la creazione/modifica di opzioni `o2o` sopra soglia
 *   e filtra le opzioni mostrate ai creator.
 */
const SCOPE_LABELS = {
  all: 'Tutti',
  post: 'Post (tutti)',
  post_image: 'Post · Immagini',
  post_video: 'Post · Video',
  post_audio: 'Post · Audio',
  live: 'Live',
  o2o: 'Chiamate',
  profile_access: 'Accesso profilo',
};

const FILTER_TABS = [
  { key: 'all_scopes', label: 'Tutti gli ambiti' },
  { key: 'o2o', label: 'Chiamate' },
  { key: 'live', label: 'Live' },
  { key: 'post_image', label: 'Post · Immagini' },
  { key: 'post_video', label: 'Post · Video' },
  { key: 'post_audio', label: 'Post · Audio' },
  { key: 'post', label: 'Post (tutti)' },
  { key: 'profile_access', label: 'Accesso profilo' },
  { key: 'all', label: 'Generici' },
];

const SCOPE_BADGE_CLASS = {
  all: 'bg-amber-500/15 gold-text',
  o2o: 'bg-emerald-500/15 text-emerald-300',
  live: 'bg-rose-500/15 text-rose-300',
  post_image: 'bg-sky-500/15 text-sky-300',
  post_video: 'bg-violet-500/15 text-violet-300',
  post_audio: 'bg-teal-500/15 text-teal-300',
  post: 'bg-white/10 text-neutral-300',
  profile_access: 'bg-orange-500/15 text-orange-300',
};

const AdminCreatorPricesPanel = () => {
  const { toast } = useToast();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draftNew, setDraftNew] = useState({ credits: 0, label: '', scope: 'all', is_active: true });
  const [creating, setCreating] = useState(false);
  const [scopeFilter, setScopeFilter] = useState('all_scopes');
  // Limite massimo O2O (crediti/min) — admin-editable.
  const [o2oLimit, setO2oLimit] = useState(null);
  const [o2oLimitDraft, setO2oLimitDraft] = useState('');
  const [savingLimit, setSavingLimit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listCreatorPriceOptions();
      setOptions(rows);
    } catch (e) {
      toast({
        title: 'Errore caricamento prezzi',
        description: e?.response?.data?.detail || String(e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadLimit = useCallback(async () => {
    try {
      const data = await getO2OPriceLimit();
      setO2oLimit(data);
      setO2oLimitDraft(String(data.max_credits));
    } catch { /* non blocking */ }
  }, []);

  useEffect(() => { load(); loadLimit(); }, [load, loadLimit]);

  const saveLimit = async () => {
    const val = parseInt(o2oLimitDraft, 10);
    if (!val || val < 1) {
      toast({ title: 'Limite non valido', description: 'Inserisci un numero di crediti ≥ 1.', variant: 'destructive' });
      return;
    }
    setSavingLimit(true);
    try {
      const data = await updateO2OPriceLimit(val);
      setO2oLimit(data);
      setO2oLimitDraft(String(data.max_credits));
      toast({ title: 'Limite O2O aggiornato', description: `Massimo ${data.max_credits} Crediti (€${data.eur_estimate.toFixed(2)}/min).` });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({
        title: 'Errore aggiornamento limite',
        description: typeof detail === 'string' ? detail.split('|').pop() : 'Riprova.',
        variant: 'destructive',
      });
    } finally { setSavingLimit(false); }
  };

  const startEdit = (opt) => {
    setEditingId(opt.id);
    setEditingDraft({ ...opt });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingDraft(null);
  };

  const saveEdit = async () => {
    if (!editingDraft) return;
    const patch = {
      credits: Number(editingDraft.credits) || 0,
      scope: editingDraft.scope,
      is_active: !!editingDraft.is_active,
      label: (editingDraft.label || '').slice(0, 64),
    };
    setSaving(true);
    try {
      await updateCreatorPriceOption(editingDraft.id, patch);
      toast({ title: 'Prezzo aggiornato' });
      await load();
      cancelEdit();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({
        title: 'Errore aggiornamento',
        description: typeof detail === 'string' ? detail.split('|').pop() : 'Riprova.',
        variant: 'destructive',
      });
    } finally { setSaving(false); }
  };

  const toggle = async (opt) => {
    try {
      await updateCreatorPriceOption(opt.id, { is_active: !opt.is_active });
      await load();
    } catch {
      toast({ title: 'Errore aggiornamento', variant: 'destructive' });
    }
  };

  const move = async (idx, dir) => {
    const next = dir === 'up' ? idx - 1 : idx + 1;
    if (next < 0 || next >= options.length) return;
    const arr = [...options];
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    setOptions(arr);
    try {
      await reorderCreatorPriceOptions(arr.map((o) => o.id));
    } catch {
      toast({ title: 'Errore riordino', variant: 'destructive' });
      load();
    }
  };

  const softDelete = async (opt) => {
    if (!window.confirm(`Disattivare la quantità ${opt.credits} Credits (${SCOPE_LABELS[opt.scope] || opt.scope})? I contenuti già pubblicati con questo prezzo rimarranno invariati.`)) return;
    try {
      await deleteCreatorPriceOption(opt.id);
      toast({ title: 'Prezzo archiviato' });
      await load();
    } catch {
      toast({ title: 'Errore archiviazione', variant: 'destructive' });
    }
  };

  const createOption = async () => {
    setCreating(true);
    try {
      await createCreatorPriceOption({
        credits: Number(draftNew.credits) || 0,
        scope: draftNew.scope,
        is_active: !!draftNew.is_active,
        label: (draftNew.label || '').slice(0, 64),
      });
      setDraftNew((prev) => ({ credits: 0, label: '', scope: prev.scope, is_active: true }));
      toast({ title: 'Prezzo creato' });
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({
        title: 'Errore creazione',
        description: typeof detail === 'string' ? detail.split('|').pop() : 'Riprova.',
        variant: 'destructive',
      });
    } finally { setCreating(false); }
  };

  const changeFilter = (key) => {
    setScopeFilter(key);
    // Il nuovo prezzo eredita l'ambito della tab selezionata.
    setDraftNew((prev) => ({ ...prev, scope: key === 'all_scopes' ? 'all' : key }));
  };

  const visibleOptions = useMemo(() => {
    if (scopeFilter === 'all_scopes') return options;
    return options.filter((o) => o.scope === scopeFilter);
  }, [options, scopeFilter]);

  const activeCount = useMemo(() => options.filter((o) => o.is_active).length, [options]);
  const canReorder = scopeFilter === 'all_scopes';

  return (
    <section className="rounded-2xl border border-border bg-card p-4" data-testid="admin-creator-prices-panel">
      <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Coins className="h-4 w-4 gold-text" />
            Prezzi Voce Pro
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Scale prezzi separate per Chiamate, Live, Post immagine/video/audio.
            Disattivare un prezzo NON modifica i contenuti già pubblicati con quel valore.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span data-testid="admin-creator-prices-count">
            <strong className="text-foreground">{options.length}</strong> totali · <strong className="text-emerald-400">{activeCount}</strong> attivi
          </span>
        </div>
      </div>

      {/* Limite massimo O2O */}
      <div className="mb-4 flex flex-col gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid="o2o-limit-box">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
          <div>
            <p className="text-sm font-semibold">Limite massimo Chiamate</p>
            <p className="text-xs text-muted-foreground">
              Tetto della tariffa al minuto per le chiamate O2O. I prezzi O2O sopra il limite vengono bloccati e nascosti ai creator.
              {o2oLimit && (
                <> Attuale: <strong className="text-foreground">{o2oLimit.max_credits} Crediti</strong> (≈ €{o2oLimit.eur_estimate.toFixed(2)}/min).</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            value={o2oLimitDraft}
            onChange={(e) => setO2oLimitDraft(e.target.value)}
            className="h-8 w-24"
            data-testid="o2o-limit-input"
          />
          <span className="text-xs text-muted-foreground">
            ≈ €{((parseInt(o2oLimitDraft, 10) || 0) * 0.1).toFixed(2)}/min
          </span>
          <Button
            size="sm"
            onClick={saveLimit}
            disabled={savingLimit}
            className="gold-gradient text-black"
            data-testid="o2o-limit-save"
          >
            {savingLimit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Salva
          </Button>
        </div>
      </div>

      {/* Tab filtro per ambito */}
      <div className="mb-3 flex flex-wrap gap-1.5" data-testid="creator-price-scope-tabs">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => changeFilter(tab.key)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
              scopeFilter === tab.key
                ? 'border-amber-500/60 bg-amber-500/15 gold-text'
                : 'border-border text-muted-foreground hover:bg-white/5'
            }`}
            data-testid={`creator-price-tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="h-5 w-5 animate-spin gold-text" /></div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-12 px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Credits</th>
                <th className="px-3 py-2 text-left">≈ EUR</th>
                <th className="px-3 py-2 text-left">Etichetta</th>
                <th className="px-3 py-2 text-left">Ambito</th>
                <th className="px-3 py-2 text-left">Stato</th>
                <th className="w-40 px-3 py-2 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleOptions.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="creator-price-empty">
                    Nessun prezzo per questo ambito. Aggiungine uno qui sotto.
                  </td>
                </tr>
              )}
              {visibleOptions.map((opt, idx) => (
                <tr key={opt.id} className={`transition-colors ${!opt.is_active ? 'opacity-60' : ''}`} data-testid={`creator-price-row-${opt.id}`}>
                  <td className="px-3 py-2">
                    {canReorder ? (
                      <div className="flex flex-col">
                        <button
                          disabled={idx === 0}
                          onClick={() => move(idx, 'up')}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                          aria-label="Sposta su"
                          data-testid={`creator-price-move-up-${opt.id}`}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </button>
                        <button
                          disabled={idx === visibleOptions.length - 1}
                          onClick={() => move(idx, 'down')}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                          aria-label="Sposta giù"
                          data-testid={`creator-price-move-down-${opt.id}`}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">{idx + 1}</span>
                    )}
                  </td>
                  {editingId === opt.id ? (
                    <>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min={0}
                          value={editingDraft.credits}
                          onChange={(e) => setEditingDraft({ ...editingDraft, credits: e.target.value })}
                          className="h-8 w-24"
                          data-testid={`creator-price-edit-credits-${opt.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        €{((Number(editingDraft.credits) || 0) * 0.1).toFixed(2)}
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={editingDraft.label || ''}
                          onChange={(e) => setEditingDraft({ ...editingDraft, label: e.target.value })}
                          placeholder="es. Free / Popolare"
                          className="h-8"
                          data-testid={`creator-price-edit-label-${opt.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={editingDraft.scope}
                          onValueChange={(v) => setEditingDraft({ ...editingDraft, scope: v })}
                        >
                          <SelectTrigger className="h-8 w-36" data-testid={`creator-price-edit-scope-${opt.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(SCOPE_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <label className="inline-flex cursor-pointer items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={!!editingDraft.is_active}
                            onChange={(e) => setEditingDraft({ ...editingDraft, is_active: e.target.checked })}
                            className="accent-amber-400"
                          />
                          Attivo
                        </label>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" onClick={saveEdit} disabled={saving} className="gold-gradient text-black" data-testid={`creator-price-save-${opt.id}`}>
                            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid={`creator-price-cancel-${opt.id}`}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 font-mono font-semibold">{opt.credits === 0 ? '0 (Free)' : opt.credits}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><Euro className="h-3 w-3" />{opt.eur_estimate.toFixed(2)}</span>
                      </td>
                      <td className="px-3 py-2 text-xs">{opt.label || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                          SCOPE_BADGE_CLASS[opt.scope] || 'bg-sky-500/15 text-sky-300'
                        }`}>
                          {SCOPE_LABELS[opt.scope] || opt.scope}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => toggle(opt)}
                          className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition ${
                            opt.is_active
                              ? 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
                              : 'border-white/20 text-muted-foreground hover:bg-white/10'
                          }`}
                          data-testid={`creator-price-toggle-${opt.id}`}
                        >
                          <Power className="h-3 w-3" />
                          {opt.is_active ? 'Attivo' : 'Inattivo'}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => startEdit(opt)} data-testid={`creator-price-edit-${opt.id}`}>
                            Modifica
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => softDelete(opt)}
                            className="text-red-300 hover:text-red-200"
                            data-testid={`creator-price-delete-${opt.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}

              {/* Inline "Add" row */}
              <tr className="bg-white/[0.02]" data-testid="creator-price-new-row">
                <td className="px-3 py-3 text-center text-muted-foreground">
                  <Plus className="mx-auto h-3 w-3" />
                </td>
                <td className="px-3 py-3">
                  <Input
                    type="number"
                    min={0}
                    value={draftNew.credits}
                    onChange={(e) => setDraftNew({ ...draftNew, credits: e.target.value })}
                    placeholder="es. 25"
                    className="h-8 w-24"
                    data-testid="creator-price-new-credits"
                  />
                </td>
                <td className="px-3 py-3 text-xs text-muted-foreground">
                  €{((Number(draftNew.credits) || 0) * 0.1).toFixed(2)}
                </td>
                <td className="px-3 py-3">
                  <Input
                    value={draftNew.label}
                    onChange={(e) => setDraftNew({ ...draftNew, label: e.target.value })}
                    placeholder="Etichetta (opz.)"
                    className="h-8"
                    data-testid="creator-price-new-label"
                  />
                </td>
                <td className="px-3 py-3">
                  <Select value={draftNew.scope} onValueChange={(v) => setDraftNew({ ...draftNew, scope: v })}>
                    <SelectTrigger className="h-8 w-36" data-testid="creator-price-new-scope">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SCOPE_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3">
                  <label className="inline-flex cursor-pointer items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={draftNew.is_active}
                      onChange={(e) => setDraftNew({ ...draftNew, is_active: e.target.checked })}
                      className="accent-amber-400"
                    />
                    Attivo
                  </label>
                </td>
                <td className="px-3 py-3 text-right">
                  <Button
                    size="sm"
                    onClick={createOption}
                    disabled={creating}
                    className="gold-gradient text-black"
                    data-testid="creator-price-new-submit"
                  >
                    {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    Aggiungi
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdminCreatorPricesPanel;
